import { useAppDispatch, useAppSelector } from 'app/config/store';
import React, { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Spin, Table, Tooltip } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import CommentComponent from 'app/entities/comment/comment';
import ModalConfirmComponent from 'app/shared/component/modal/confirm';
import ModalRejectComponent from 'app/shared/component/modal/reject';
import { FuncTypes, funcTypeToString } from 'app/shared/model/enum/func-type.enum';
import { formatPoint } from 'app/shared/util/percent';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import { Container } from 'reactstrap';
import { columnsResult, minusPoints, plusPoints } from '../config';
import OtherGeneralCommentComponent from '../other-comment';
import { confirmComplete, getCheckConfirmComplete, getOtherComment, getOtherMinusComment, getOtherPlusComment, rejectProcess, reset, saveDraft } from './confirm-complete.reducer';
import OtherCommentEvaluationComponent from './other-comment';
import { exportExcel } from 'app/entities/evaluation-level/evaluation-level.reducer';
import { saveAs } from 'file-saver';
import { getCurrentPlanPeriodOpen } from 'app/entities/common/common.reducer';

type IConfirmCompleteEvaluationProps = {
    isOpen: boolean;
    onClose: (isReload?: boolean) => void;
    accountTableName: string;
    accountTableId: string;
    functionType: FuncTypes;
};

export const CheckConfirmCompleteEvaluation = ({ isOpen, onClose, accountTableName, accountTableId, functionType }: IConfirmCompleteEvaluationProps) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const updating = useAppSelector(state => state.checkConfirmCompleteEvaluation.updating);
    const loading = useAppSelector(state => state.checkConfirmCompleteEvaluation.loading);
    const entities = useAppSelector(state => state.checkConfirmCompleteEvaluation.entities);
    const otherComment = useAppSelector(state => state.checkConfirmCompleteEvaluation.otherComment);
    const updateSuccess = useAppSelector(state => state.checkConfirmCompleteEvaluation.updateSuccess);
    const account = useAppSelector(state => state.authentication.account);
    const currentPlanPeriod = useAppSelector(state => state.common.currentPlanPeriod);
    const currentStatusProcess = useAppSelector(state => state.categoryEvaluation.entity);

    // Init state
    const [isOpenDrawerComment, setOpenDrawerComment] = useState(false);
    const [isOpenModalConfirmComplete, setOpenModalConfirmComplete] = useState(false);
    const [isOpenModalRejectProcess, setOpenModalRejectProcess] = useState(false);
    const [isOpenModalOtherComment, setOpenModalOtherComment] = useState(false);
    const [currentIndexCriteria, setCurrentIndexCriteria] = useState(null);
    const [isOpenModalOtherGeneralComment, setOpenModalOtherGeneralComment] = useState(false);

    const [isLoadingComment, setLoadingComment] = useState(false);

    useEffect(() => {
        if (accountTableId) innitData();
    }, [accountTableId]);

    useEffect(() => {
        form.setFieldsValue({
            generalFeedback: entities?.evaluateOfLevel?.generalFeedback,
            plusPoint: mapPoints(entities?.selfEvaluation?.plusPoint, entities?.evaluateOfLevel?.plusPoint),
            minusPoint: mapPoints(entities?.selfEvaluation?.minusPoint, entities?.evaluateOfLevel?.minusPoint),
        });
    }, [entities]);

    useEffect(() => {
        if (updateSuccess) {
            handleClose(updateSuccess);
        }
    }, [updateSuccess]);

    const mapPoints = (points, comments = []) =>
        points?.map((x, idx) => ({
            ...x,
            point: x.point && formatPoint(x.point),
            checkExplain: comments && comments[idx]?.explain
        }));

    const calculateTotalWithFormat = (points) =>
        formatPoint(_.sumBy(points, (o: any) => o.point));

    const innitData = () => {
        dispatch(getCheckConfirmComplete({ ownerId: accountTableId, reportScheduleId: currentPlanPeriod?.value, userId: account?.id, planPeriodId: currentPlanPeriod?.value }));
    };

    const mapperDataPayload = (values) => {
        const payload = {
            ...values,
            ownerId: accountTableId,
            userId: account?.id,
            planPeriodId: currentPlanPeriod?.value,
            evaluateLevelId: entities?.evaluateLevelId,
            checkinPoint: entities?.krYearStatistic?.checkinPoint,
            year: currentPlanPeriod?.label,
            documentId: entities?.documentId,
            plusPoint: values?.plusPoint?.map(x => {
                return {
                    explain: x.checkExplain
                }
            }),
            minusPoint: values?.minusPoint?.map(x => {
                return {
                    explain: x.checkExplain
                }
            }),
        };
        return payload;
    }

    const handleSubmit = () => {
        handleOpenModalConfirmComplete()
    };

    const handleSaveDraft = () => {
        form.validateFields().then(values => {
            const payload = mapperDataPayload(values);
            dispatch(saveDraft(payload)).then((res) => {
                if (res && res.payload) {
                    handleClose();
                }
            });
        }).catch(() => { return; });
    };

    const handleReject = () => {
        handleOpenModalRejectProcess()
    }

    const handleClose = (reload = false) => {
        onClose(reload);
        dispatch(reset())
    };

    const handleShowDrawerComment = () => {
        setOpenDrawerComment(true);
    };

    const handleCloseDrawerComment = () => {
        setOpenDrawerComment(false);
    };

    const handleOpenModalConfirmComplete = () => {
        setOpenModalConfirmComplete(true);
    }

    const handleConfirmComplete = () => {
        setOpenModalConfirmComplete(false);
        form.validateFields().then(values => {
            const payload = mapperDataPayload(values);
            dispatch(confirmComplete(payload));
        }).catch(() => { return; });
    }

    const handleOpenModalRejectProcess = () => {
        setOpenModalRejectProcess(true);
    }

    const handleCancelProcess = (reason: string) => {
        setOpenModalRejectProcess(false);
        form.validateFields().then(async values => {
            const payload = { ...mapperDataPayload(values), reason };
            await dispatch(rejectProcess(payload));
            handleClose(true);
        }).catch(() => { return; });
    }

    const handleOpenModalOtherComment = () => {
        setOpenModalOtherComment(true);
    }

    const handleCloseModalOtherComment = () => {
        setOpenModalOtherComment(false);
    }

    const handleGetOtherPlusComments = (index: number) => {
        setCurrentIndexCriteria(index);
        handleOpenModalOtherComment()
        setLoadingComment(true)
        dispatch(getOtherPlusComment({ index, ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleGetOtherMinusComments = (index: number) => {

        setCurrentIndexCriteria(index);
        setLoadingComment(true)
        handleOpenModalOtherComment()
        dispatch(getOtherMinusComment({ index, ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleOpenModalOtherGeneralComment = () => {
        setOpenModalOtherGeneralComment(true);
    }

    const handleCloseModalOtherGeneralComment = () => {
        setOpenModalOtherGeneralComment(false);
    }

    const handleGetOtherComments = () => {
        setLoadingComment(true)
        handleOpenModalOtherGeneralComment()
        dispatch(getOtherComment({ ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleExport = () => {
        dispatch(
            exportExcel({ ownerId: accountTableId, reportScheduleId: currentPlanPeriod?.value })).then(res => {
                if (res.payload) {
                    const resultExcel = res.payload as any;
                    saveAs(
                        new Blob([resultExcel.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                        `Kết quả_${dayjs().format('DDMMYYYYhhmmss')}`,
                    );
                }
            });
    }

    return (
        <Modal
            width={1150}
            open={isOpen}
            footer={null}
            onCancel={() => {
                handleClose();
            }}
            maskClosable={false}
        >
            <Container className="form">
                <Spin spinning={loading || updating} delay={300}>
                    <Form initialValues={{
                        plusPoint: plusPoints,
                        minusPoint: minusPoints
                    }} size="large" form={form} onFinish={handleSubmit} layout="vertical">
                        <Row>
                            <Col md={20}>
                                <Title level={3}>{`Xác nhận hoàn thành ${funcTypeToString(currentStatusProcess?.functionKey)} ${accountTableName ? `- ${accountTableName}` : ''}`}</Title>
                            </Col>
                            <Col md={1} />
                            <Col md={3}><Button onClick={handleShowDrawerComment}>Ý kiến trao đổi</Button></Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <Form.Item label="Kỳ báo cáo">
                                    <Input disabled value={currentPlanPeriod?.label} />
                                </Form.Item>
                            </Col>
                            <Col md={18} />
                            {/* <Col md={3}><Button className='green' onClick={handleExport}>Xuất Excel</Button></Col> */}

                        </Row>
                        <Row className='my-3'>
                            <Col md={11}>
                                <span className="fw-bold h5">I. ĐIỂM ĐÁNH GIÁ THỰC HIỆN MỤC TIÊU</span>
                            </Col>
                            <Col md={1} />
                            <Col md={3}>
                                <Form.Item>
                                    <InputNumber value={formatPoint(entities?.krYearStatistic?.checkinPoint)}
                                        disabled className='w-100' addonAfter="%" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-3'>
                            <Col md={24}>
                                <span className="fw-bold h6">a. Xem thống kế số lượng theo tình trạng thực hiện</span>
                            </Col>
                        </Row>
                        <Row className='ps-3'>
                            <Col md={24}>
                                <Table
                                    pagination={false}
                                    bordered
                                    dataSource={entities?.krYearStatistic?.krStatistics.map(x => ({ ...x, key: x.name }))}
                                    columns={columnsResult}
                                    className="w-100"
                                    rowClassName={(record, index) => {
                                        return index === entities?.krYearStatistic?.krStatistics.length - 1 ? 'fw-bold' : '';
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row className='my-3 ps-2'>
                            <Col>
                                <span className="fw-bold h6">1. ĐIỂM CỘNG</span>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={24}>
                                <Row>
                                    <Col md={18}>
                                        <Row>
                                            <Col md={11} />
                                            <Col md={1} />
                                            <Col md={5}>
                                                <Form.Item label="Đơn vị tự đánh giá">
                                                    <InputNumber value={calculateTotalWithFormat(entities?.selfEvaluation?.plusPoint)} disabled className='w-100' addonAfter="%" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col md={1} />
                                    <Col md={5}>
                                        <Form.Item label={`${entities?.evaluateLevelName} - ${entities?.performerName}`} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Form.List name="plusPoint">
                            {(fields) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row key={key} className='my-3 ps-3'>
                                            <Col md={8}>
                                                <span className="fw-bold">{`${name + 1}. ${plusPoints[name].name}`}</span>
                                                <span className='ps-2'>
                                                    <Tooltip title="Xem ý kiến khác">
                                                        <FontAwesomeIcon
                                                            onClick={() => {
                                                                handleGetOtherPlusComments(name);
                                                            }}
                                                            icon="eye"
                                                            color='blue'
                                                        />
                                                    </Tooltip>
                                                </span>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={3}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'point']}
                                                >
                                                    <InputNumber disabled className='w-100' min={1} addonAfter="%" />
                                                </Form.Item>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'explain']}>
                                                    <TextArea disabled className='w-100' rows={1} />
                                                </Form.Item>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'checkExplain']}>
                                                    <TextArea placeholder='Điền ý kiến' className='w-100' rows={1} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                </>
                            )}
                        </Form.List>
                        <Row className='my-3 ps-2'>
                            <Col md={8}>
                                <span className="fw-bold h6">2. ĐIỂM TRỪ</span>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={24}>
                                <Row>
                                    <Col md={18}>
                                        <Row>
                                            <Col md={11} />
                                            <Col md={1} />
                                            <Col md={5}>
                                                <Form.Item label="Đơn vị tự đánh giá">
                                                    <InputNumber value={calculateTotalWithFormat(entities?.selfEvaluation?.minusPoint)} disabled className='w-100' addonAfter="%" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col md={1} />
                                    <Col md={5}>
                                        <Form.Item label={`${entities?.evaluateLevelName} - ${entities?.performerName}`} />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Form.List name="minusPoint">
                            {(fields) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Row key={key} className='my-3 ps-3'>
                                            <Col md={8}>
                                                <span className="fw-bold">{`${name + 1}. ${minusPoints[name].name}`}</span>
                                                <span className='ps-2'>
                                                    <Tooltip title="Xem ý kiến khác">
                                                        <FontAwesomeIcon
                                                            onClick={() => {
                                                                handleGetOtherMinusComments(name);
                                                            }}
                                                            icon="eye"
                                                            color='blue'
                                                        />
                                                    </Tooltip>
                                                </span>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={3}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'point']}>
                                                    <InputNumber disabled className='w-100' min={1} addonAfter="%" />
                                                </Form.Item>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'explain']}>
                                                    <TextArea className='w-100' disabled rows={1} />
                                                </Form.Item>
                                            </Col>
                                            <Col md={1} />
                                            <Col md={5}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'checkExplain']}>
                                                    <TextArea placeholder='Điền ý kiến' className='w-100' rows={1} />
                                                </Form.Item>
                                            </Col>

                                        </Row>
                                    ))}
                                </>
                            )}
                        </Form.List>
                        <hr />
                        <Row className='my-3'>
                            <Col md={24}>
                                <span className="fw-bold h5">II. TỔNG HỢP KẾT QUẢ ĐÁNH GIÁ</span>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={8}>
                                <span className="fw-bold h6">1. TỔNG ĐIỂM ĐÁNH GIÁ</span>
                            </Col>
                            <Col md={1} />
                            <Col md={3}>
                                <Form.Item
                                >
                                    <InputNumber value={formatPoint(entities?.selfEvaluation?.pointTotal)} disabled className='w-100' addonAfter="%" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={8}>
                                <span className="fw-bold h6">2. KẾT QUẢ XẾP LOẠI THEO TỔNG ĐIỂM</span>
                            </Col>
                            <Col md={1} />
                            <Col md={6}>
                                <Form.Item>
                                    <Input disabled value={`${entities?.selfEvaluation?.rankCode} - ${entities?.selfEvaluation?.rankName}`} className='w-100' />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row className='my-3 ps-2'>
                            <Col md={8}>
                                <span className="fw-bold h6">3. KẾT QUẢ XẾP LOẠI CUỐI CÙNG</span>
                            </Col>
                            <Col md={1} />
                            <Col md={6}>
                                <Form.Item
                                >
                                    <Input disabled value={entities?.selfEvaluation?.userRankingEvaluateName} className='w-100' />
                                </Form.Item>
                            </Col>
                        </Row>
                        <hr />
                        <Row className='my-3'>
                            <Col>
                                <span className="fw-bold h5">III. Ý KIẾN CHUNG</span>
                                <span className='ps-2'>
                                    <Tooltip title="Xem ý kiến khác">
                                        <FontAwesomeIcon
                                            onClick={() => {
                                                handleGetOtherComments();
                                            }}
                                            icon="eye"
                                            color='blue'
                                        />
                                    </Tooltip>
                                </span>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={24}>
                                <Form.Item label="Đơn vị được đánh giá">
                                    <TextArea disabled className='w-100' value={entities?.selfEvaluation?.generalFeedback} rows={4} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={24}>
                                <Form.Item label={`${entities?.evaluateLevelName} - ${entities?.performerName}`} name={'generalFeedback'}>
                                    <TextArea className='w-100' rows={4} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Row className="action" justify="end">
                                <Col>
                                    <Button
                                        onClick={() => {
                                            handleClose();
                                        }}
                                    >
                                        Hủy
                                    </Button>
                                </Col>
                                <Col>
                                    <Button
                                        className="green"
                                        onClick={() => {
                                            handleSaveDraft();
                                        }}
                                        disabled={updating}
                                    >
                                        Lưu nháp
                                    </Button>
                                </Col>
                                <Col>
                                    {entities?.isFinalEvaluateLevel ?
                                        <Button type="primary" htmlType="submit" disabled={updating}>
                                            {`Hoàn thành ${funcTypeToString(currentStatusProcess?.functionKey)}`}
                                        </Button> :
                                        <Button type="primary" htmlType="submit" disabled={updating}>
                                            {`Hoàn thành ${funcTypeToString(currentStatusProcess?.functionKey)} và gửi cấp tiếp theo`}
                                        </Button>
                                    }
                                </Col>
                                {functionType !== FuncTypes.Appraisal &&
                                    <Col>
                                        <Button className='warning'
                                            onClick={() => {
                                                handleReject();
                                            }} disabled={updating}>
                                            {`Hoàn thành ${funcTypeToString(currentStatusProcess?.functionKey)} và yêu cầu sửa lại`}
                                        </Button>
                                    </Col>
                                }
                            </Row>
                        </Form.Item>
                    </Form>
                    {isOpenDrawerComment &&
                        <CommentComponent topicId={entities?.documentId} handleDrawerComment={handleCloseDrawerComment} isOpenDrawerComment={isOpenDrawerComment} />
                    }
                    <ModalConfirmComponent
                        visible={isOpenModalConfirmComplete}
                        onCancel={() => {
                            setOpenModalConfirmComplete(false);
                        }}
                        onConfirm={handleConfirmComplete}
                        isLoading={updating}
                        title={`Hoàn thành ${funcTypeToString(currentStatusProcess?.functionKey)} dữ liệu?`}
                    />
                    <ModalRejectComponent
                        visible={isOpenModalRejectProcess}
                        onCancel={() => {
                            setOpenModalRejectProcess(false);
                        }}
                        onReject={handleCancelProcess}
                        title={`Yêu cầu sửa lại`}
                    />
                    {isOpenModalOtherComment && <OtherCommentEvaluationComponent
                        data={otherComment}
                        isLoading={isLoadingComment}
                        nameCriteria={plusPoints[currentIndexCriteria].name}
                        isOpen={isOpenModalOtherComment} onClose={handleCloseModalOtherComment} />}

                    {isOpenModalOtherGeneralComment && <OtherGeneralCommentComponent
                        data={otherComment}
                        isLoading={isLoadingComment}
                        isOpen={isOpenModalOtherGeneralComment} onClose={handleCloseModalOtherGeneralComment} />}
                </Spin>
            </Container>
        </Modal >
    );
};

export default CheckConfirmCompleteEvaluation;
